import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  makeStyles,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  Grid
} from '@material-ui/core';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import StripeCheckout from './StripeCheckout/StripeCheckout';
import ComplianceBadges from './ComplianceBadges';

const useStyles = makeStyles((theme) => ({
  page: {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)',
    padding: theme.spacing(4, 2),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(2)
  },
  headerRow: {
    width: '100%',
    maxWidth: 820,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between'
  },
  heading: { fontWeight: 700, fontSize: '1.6rem', color: '#e5e5e5' },
  sub: { color: '#b0b0b0' },

  card: {
    width: '100%',
    maxWidth: 820,
    border: '1px solid #3a3a3a',
    borderRadius: 14,
    background: 'linear-gradient(145deg, #2a2a2a, #1f1f1f)',
    padding: theme.spacing(2),
    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.4)'
  },

  plansGrid: {
    marginTop: theme.spacing(1)
  },

  planCard: {
    border: '1px solid #4a4a4a',
    borderRadius: 12,
    padding: theme.spacing(2),
    cursor: 'pointer',
    background: '#3a3a3a',
    transition: 'all .3s ease',
    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.3)',
    minHeight: 140,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    [theme.breakpoints.down('sm')]: {
      minHeight: 110
    },
    '&:hover': {
      background: '#404040',
      boxShadow: '0 0 16px rgba(100, 100, 255, 0.25)',
      borderColor: '#6464ff',
      transform: 'translateY(-2px)'
    }
  },
  planCardSelected: {
    border: '1.5px solid #6464ff',
    background: '#404040',
    boxShadow: '0 0 20px rgba(100, 100, 255, 0.4)',
    transform: 'translateY(-2px)'
  },

  planLabel: { fontWeight: 700, fontSize: '1rem', color: '#e5e5e5' },
  planDesc: { color: '#b0b0b0', marginTop: 4, fontSize: '.9rem' },
  planPrice: { marginTop: 8, fontWeight: 700, color: '#8484ff' },

  dividerNote: {
    marginTop: theme.spacing(2),
    color: '#909090',
    fontSize: 13,
    textAlign: 'center'
  },

  payWrap: {
    marginTop: theme.spacing(2),
    borderTop: '1px dashed #4a4a4a',
    paddingTop: theme.spacing(2)
  },

  termsLink: {
    marginTop: theme.spacing(1),
    textAlign: 'center',
    '& > button': {
      textTransform: 'none',
      color: '#8484ff',
      '&:hover': {
        color: '#a4a4ff',
        background: 'rgba(100, 100, 255, 0.08)'
      }
    }
  }
}));

// Service packages
const plans = [
  { id: 0, label: 'Starter Package', description: 'Basic tutoring package', price: 10 },
  { id: 1, label: 'Standard Package', description: 'Premium tutoring package', price: 35 },
  { id: 2, label: 'Professional Package', description: 'Advanced learning package', price: 50 },
  { id: 3, label: '120 Hours', description: 'Complete learning package', price: 170 },
  { id: 4, label: '240 Hours', description: 'All-inclusive package', price: 300 },
  { id: 5, label: '500 Hours', description: 'Ultimate mentorship package', price: 550 },
  { id: 6, label: '1000 Hours', description: 'Lifetime access package', price: 1000 },
  { id: 7, label: '2000 Hours', description: 'Elite developer package', price: 1600 }
];

const Edmond = () => {
  const classes = useStyles();
  const [selected, setSelected] = useState(null);
  const [infoOpen, setInfoOpen] = useState(false);
  const [infoTab, setInfoTab] = useState(0); // 0 = About, 1 = Terms
  const [autoCheckTerms, setAutoCheckTerms] = useState(false);
  const paymentRef = React.useRef(null);
  const selectedItem = plans.find((s) => s.id === selected);

  const handleSelectPlan = (id) => {
    setSelected(id);
    setAutoCheckTerms(true);
    setTimeout(() => {
      paymentRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setAutoCheckTerms(false);
    }, 100);
  };

  return (
    <Box className={classes.page}>
      <div className={classes.headerRow}>
        <div>
          <Typography className={classes.heading}>Make Your Payment Here</Typography>
          <Typography className={classes.sub}>Secure checkout. Select your package below.</Typography>
        </div>
        <IconButton
          onClick={() => {
            setInfoTab(0);
            setInfoOpen(true);
          }}
          style={{ color: '#8484ff' }}
          title='About services & terms'>
          <InfoOutlinedIcon />
        </IconButton>
      </div>

      <ComplianceBadges />

      <Paper className={classes.card} elevation={0}>
        <Typography align='center' style={{ fontWeight: 700, color: '#e5e5e5', marginBottom: 16 }}>
          Select Your Package
        </Typography>

        <Grid container spacing={3} className={classes.plansGrid}>
          <Grid item xs={12} sm={6} md={3}>
            <Paper
              elevation={0}
              className={`${classes.planCard} ${selected === 0 ? classes.planCardSelected : ''}`}
              onClick={() => handleSelectPlan(0)}>
              <div>
                <Typography className={classes.planLabel}>{plans[0].label}</Typography>
                <Typography className={classes.planDesc}>{plans[0].description}</Typography>
              </div>
              <Typography className={classes.planPrice}>€{plans[0].price}</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Paper
              elevation={0}
              className={`${classes.planCard} ${selected === 1 ? classes.planCardSelected : ''}`}
              onClick={() => handleSelectPlan(1)}>
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <Typography className={classes.planLabel}>{plans[1].label}</Typography>
                  <span style={{ background: '#6464ff', color: '#fff', padding: '2px 8px', borderRadius: 4, fontSize: '0.75rem', fontWeight: 600 }}>
                    POPULAR
                  </span>
                </div>
                <Typography className={classes.planDesc}>{plans[1].description}</Typography>
              </div>
              <Typography className={classes.planPrice}>€{plans[1].price}</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Paper
              elevation={0}
              className={`${classes.planCard} ${selected === 2 ? classes.planCardSelected : ''}`}
              onClick={() => handleSelectPlan(2)}>
              <div>
                <Typography className={classes.planLabel}>{plans[2].label}</Typography>
                <Typography className={classes.planDesc}>{plans[2].description}</Typography>
              </div>
              <Typography className={classes.planPrice}>€{plans[2].price}</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Paper
              elevation={0}
              className={`${classes.planCard} ${selected === 3 ? classes.planCardSelected : ''}`}
              onClick={() => handleSelectPlan(3)}>
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <Typography className={classes.planLabel}>{plans[3].label}</Typography>
                  <span
                    style={{
                      background: 'linear-gradient(135deg, #8b5cf6, #6366f1)',
                      color: '#fff',
                      padding: '2px 8px',
                      borderRadius: 4,
                      fontSize: '0.75rem',
                      fontWeight: 700,
                      boxShadow: '0 0 10px rgba(139, 92, 246, 0.5)'
                    }}>
                    BEST VALUE
                  </span>
                </div>
                <Typography className={classes.planDesc}>{plans[3].description}</Typography>
              </div>
              <Typography className={classes.planPrice}>€{plans[3].price}</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Paper
              elevation={0}
              className={`${classes.planCard} ${selected === 4 ? classes.planCardSelected : ''}`}
              onClick={() => handleSelectPlan(4)}>
              <div>
                <Typography className={classes.planLabel}>{plans[4].label}</Typography>
                <Typography className={classes.planDesc}>{plans[4].description}</Typography>
              </div>
              <Typography className={classes.planPrice}>€{plans[4].price}</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Paper
              elevation={0}
              className={`${classes.planCard} ${selected === 5 ? classes.planCardSelected : ''}`}
              onClick={() => handleSelectPlan(5)}>
              <div>
                <Typography className={classes.planLabel}>{plans[5].label}</Typography>
                <Typography className={classes.planDesc}>{plans[5].description}</Typography>
              </div>
              <Typography className={classes.planPrice}>€{plans[5].price}</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Paper
              elevation={0}
              className={`${classes.planCard} ${selected === 6 ? classes.planCardSelected : ''}`}
              onClick={() => handleSelectPlan(6)}>
              <div>
                <Typography className={classes.planLabel}>{plans[6].label}</Typography>
                <Typography className={classes.planDesc}>{plans[6].description}</Typography>
              </div>
              <Typography className={classes.planPrice}>€{plans[6].price}</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Paper
              elevation={0}
              className={`${classes.planCard} ${selected === 7 ? classes.planCardSelected : ''}`}
              onClick={() => handleSelectPlan(7)}>
              <div>
                <Typography className={classes.planLabel}>{plans[7].label}</Typography>
                <Typography className={classes.planDesc}>{plans[7].description}</Typography>
              </div>
              <Typography className={classes.planPrice}>€{plans[7].price}</Typography>
            </Paper>
          </Grid>
        </Grid>

        <Typography className={classes.dividerNote}>
          Select a plan to continue. The Pay button stays grey until you accept terms in the form.
        </Typography>

        <div className={classes.termsLink}>
          <Button
            color='primary'
            onClick={() => {
              setInfoTab(1);
              setInfoOpen(true);
            }}>
            View Terms & Conditions
          </Button>
        </div>

        {selectedItem && (
          <div ref={paymentRef} className={classes.payWrap}>
            {/* Stripe form handles the disabled Pay state until terms are accepted */}
            <StripeCheckout price={selectedItem.price * 1.05} description={selectedItem.description} autoCheckTerms={autoCheckTerms} />
          </div>
        )}
      </Paper>

      {/* Info / Terms dialog */}
      <Dialog
        open={infoOpen}
        onClose={() => setInfoOpen(false)}
        maxWidth='md'
        fullWidth
        PaperProps={{
          style: {
            background: 'linear-gradient(145deg, #2a2a2a, #1f1f1f)',
            color: '#e5e5e5',
            border: '1px solid #4a4a4a'
          }
        }}>
        <DialogTitle style={{ color: '#e5e5e5', borderBottom: '1px solid #4a4a4a' }}>Service Information</DialogTitle>
        <Tabs value={infoTab} onChange={(_, v) => setInfoTab(v)} indicatorColor='primary' textColor='primary' variant='fullWidth'>
          <Tab label='About the Services' />
          <Tab label='Terms & Conditions' />
        </Tabs>
        <DialogContent dividers>
          {infoTab === 0 && (
            <>
              <Typography variant='h6' gutterBottom>
                What you get
              </Typography>
              <Typography paragraph>
                Professional remote <strong>computer support</strong> and <strong>online programming guidance</strong>. Sessions use video call or
                screen-share. We help with setup, troubleshooting, optimization, code reviews, and developer mentorship.
              </Typography>
              <Typography variant='h6' gutterBottom>
                How it works
              </Typography>
              <Typography component='div'>
                <ul>
                  <li>Select the plan that fits your needs.</li>
                  <li>Complete payment securely via Stripe.</li>
                  <li>We’ll follow up by email to schedule your session(s).</li>
                </ul>
              </Typography>
              <Typography variant='h6' gutterBottom>
                Support scope
              </Typography>
              <Typography component='div'>
                <ul>
                  <li>PC/Mac software setup, performance checks, and troubleshooting.</li>
                  <li>Programming help: concepts, debugging, code structure, reviews.</li>
                  <li>No physical goods, on-site work, or restricted services.</li>
                </ul>
              </Typography>
            </>
          )}
          {infoTab === 1 && (
            <>
              <Typography variant='h6' gutterBottom>
                Terms (summary)
              </Typography>
              <Typography component='div'>
                <ul>
                  <li>Remote services only; no physical products.</li>
                  <li>Sessions are scheduled by email after payment.</li>
                  <li>Free reschedule/cancel ≥ 24h before; within 24h a fee up to 100% may apply.</li>
                  <li>Completed time is non-refundable; unused prepaid time can be rescheduled within 60 days.</li>
                  <li>
                    Use must comply with{' '}
                    <a href='https://stripe.com/restricted-businesses' target='_blank' rel='noopener noreferrer'>
                      Stripe’s Restricted Businesses
                    </a>
                    .
                  </li>
                  <li>
                    Payments processed by Stripe; see{' '}
                    <a href='https://stripe.com/privacy' target='_blank' rel='noopener noreferrer'>
                      Stripe Privacy
                    </a>
                    .
                  </li>
                </ul>
              </Typography>
              <Typography variant='body2' color='textSecondary'>
                Full Terms & Privacy are also shown inside the payment form.
              </Typography>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInfoOpen(false)} color='primary'>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Edmond;
